import type { VeosDocumentRow } from '../../api/documentsService';

/** Content types that mark a `DOC` row as a *received* email (Outlook / EML). */
const RECEIVED_EMAIL_CONTENT_TYPES = [
  'application/vnd.ms-outlook',
  'message/rfc822',
];

export type DocumentKind = 'pdf' | 'image' | 'email' | 'other';

/** Extensions → MIME, used when a file carries no explicit (or only a generic) type. */
export const EXTENSION_MIME: Record<string, string> = {
  pdf: 'application/pdf',
  png: 'image/png',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  gif: 'image/gif',
  bmp: 'image/bmp',
  webp: 'image/webp',
  tif: 'image/tiff',
  tiff: 'image/tiff',
  txt: 'text/plain',
  csv: 'text/csv',
  html: 'text/html',
  htm: 'text/html',
  xml: 'application/xml',
  json: 'application/json',
  zip: 'application/zip',
  doc: 'application/msword',
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  xls: 'application/vnd.ms-excel',
  xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  ppt: 'application/vnd.ms-powerpoint',
  pptx: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  msg: 'application/vnd.ms-outlook',
  eml: 'message/rfc822',
};

/**
 * A file name without its trailing extension: `"devis-2025.pdf"` → `"devis-2025"`.
 * A leading-dot name (`".gitignore"`) or an extension-less name is returned
 * unchanged, and surrounding whitespace is trimmed.
 */
export function stripFileExtension(fileName: string | undefined | null): string {
  const trimmed = (fileName ?? '').trim();
  const dot = trimmed.lastIndexOf('.');
  return dot > 0 ? trimmed.slice(0, dot) : trimmed;
}

/** MIME implied by a file name's extension, or `undefined` when unknown. */
export function mimeFromExtension(fileName: string | undefined): string | undefined {
  if (!fileName) return undefined;
  const dot = fileName.lastIndexOf('.');
  if (dot < 0) return undefined;
  const ext = fileName.slice(dot + 1).toLowerCase();
  return EXTENSION_MIME[ext];
}

/**
 * Content types too generic to tell the browser how to render a file — many
 * sending mail clients (and VEOS) label real PDFs/images with one of these.
 */
const GENERIC_CONTENT_TYPES = new Set([
  '', 'application/octet-stream', 'binary/octet-stream',
  'application/binary', 'application/download',
  'application/force-download', 'content/unknown',
]);

/** True when a server-declared content type is too generic to drive how a file renders. */
function isGenericContentType(ct: string): boolean {
  return GENERIC_CONTENT_TYPES.has(ct.trim().toLowerCase());
}

/**
 * Content type to *open/render* a file with. A specific declared type wins;
 * when the server only gave a generic octet-stream (or nothing) fall back to
 * the type implied by the file-name extension, so a PDF/image renders inline
 * instead of downloading as an unnamed blob.
 */
export function resolveContentType(
  name: string,
  declaredType: string | null | undefined,
): string {
  const declared = (declaredType ?? '').trim();
  if (declared && !isGenericContentType(declared)) return declared;
  return mimeFromExtension(name) ?? (declared || 'application/octet-stream');
}

export function isReceivedEmailContentType(contentType: string | undefined | null): boolean {
  if (!contentType) return false;
  const lower = contentType.toLowerCase();
  return RECEIVED_EMAIL_CONTENT_TYPES.some((ct) => lower.includes(ct));
}

/** Classify a document row for icon + detail-view selection. */
export function getDocumentKind(row: VeosDocumentRow): DocumentKind {
  if (row.API === 'EMAIL') return 'email';
  if (isReceivedEmailContentType(row.CONTENT_TYPE)) return 'email';

  const ct = row.CONTENT_TYPE.toLowerCase();
  if (ct.includes('pdf')) return 'pdf';
  if (ct.startsWith('image/')) return 'image';
  return 'other';
}

/**
 * Whether the Email card can reply to this document (the "Répondre" button,
 * mahhp 22227): a RECEIVED e-mail filed in the GED — a mail-kind row with
 * `API === 'DOC'` (the `.msg` / `.eml` documents the "En réponse à" select
 * lists via `WS2_GS_SEL_EMAIL_RECU`, keyed by the same `ID_ADOC`). E-mails
 * sent from VEOS (`API === 'EMAIL'`) and non-mail documents are not.
 */
export function isReplyableEmail(row: VeosDocumentRow): boolean {
  return row.API === 'DOC' && getDocumentKind(row) === 'email';
}

/**
 * Short, uppercase type label shown next to a row's kind icon:
 * "PDF" / "IMAGE" / "MAIL" / "FICHIER".
 */
export function getDocumentTypeLabel(row: VeosDocumentRow): string {
  const kind = getDocumentKind(row);
  if (kind === 'email') return 'MAIL';
  if (kind === 'image') return 'IMAGE';
  if (kind === 'pdf') return 'PDF';
  return 'FICHIER';
}

/**
 * Direction of a document: `"entrant"` (received) or `"sortant"` (sent), or
 * `null` when VEOS did not provide a `SENS` (older rows). The raw value is
 * `"ENTRANT"` / `"SORTANT"`; comparison is case-insensitive + trimmed.
 */
export function getDocumentSens(row: VeosDocumentRow): 'entrant' | 'sortant' | null {
  const raw = (row.SENS ?? '').trim().toLowerCase();
  if (raw === 'entrant') return 'entrant';
  if (raw === 'sortant') return 'sortant';
  return null;
}

/**
 * Name of the user who filed the document ("Créateur" column). Reads
 * `NOM_PER_USER` — the field name from the ticket's explicit definition — and
 * falls back to the `NOM_PER_SER` spelling that appears in some of the ticket's
 * examples, so whichever VEOS actually returns wins. Returns `''` when absent.
 */
export function getDocumentCreator(row: VeosDocumentRow): string {
  const alt = (row as { NOM_PER_SER?: string | null }).NOM_PER_SER;
  return (row.NOM_PER_USER ?? alt ?? '').trim();
}

/**
 * True when the document is attached to the current task (`DOC_TACHE === 1`) —
 * i.e. one of the documents that gave rise to the task. Drives the star +
 * "Nouveau" marker in the "Statut" column. Tolerates a string `"1"` in case
 * VEOS serialises the flag as text.
 */
export function isTaskLinkedDocument(row: VeosDocumentRow): boolean {
  const flag = row.DOC_TACHE as number | string | null | undefined;
  return flag === 1 || flag === '1';
}

/** `dd/mm/yy[yy]` (also `-` / `.` separators) + an optional `HH:mm[:ss]` / `HHhmm` time. */
const FR_DATE_RE =
  /^(\d{1,2})[/.-](\d{1,2})[/.-](\d{4}|\d{2})(?!\d)(?:\D+?(\d{1,2})[:h](\d{2})(?::(\d{2}))?)?/;
/** ISO `yyyy-mm-dd` + an optional `THH:mm[:ss]` / ` HH:mm[:ss]` time. */
const ISO_DATE_RE = /^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2})(?::(\d{2}))?)?/;

/**
 * A 2-digit year resolves to the century that keeps it at most this many years
 * in the future (GED documents are dated in the past): with 2026 as the
 * reference, `20` → 2020, `36` → 2036, `95` → 1995.
 */
const TWO_DIGIT_YEAR_FUTURE_WINDOW = 10;

function pad2(value: number): string {
  return String(value).padStart(2, '0');
}

function buildSortableDate(
  year: number,
  month: number,
  day: number,
  hours: string | undefined,
  minutes: string | undefined,
  seconds: string | undefined,
): string | null {
  if (month < 1 || month > 12 || day < 1 || day > 31) return null;
  const hh = Number(hours ?? 0);
  const mi = Number(minutes ?? 0);
  const ss = Number(seconds ?? 0);
  if (hh > 23 || mi > 59 || ss > 59) return null;
  return `${String(year).padStart(4, '0')}-${pad2(month)}-${pad2(day)}T${pad2(hh)}:${pad2(mi)}:${pad2(ss)}`;
}

/**
 * Chronologically sortable key for a document date (`DAT_ADOC`, mahhp 22227).
 *
 * VEOS returns `DAT_ADOC` as a French **display** string — `05/06/20` or
 * `15/01/2024` (2- or 4-digit year), occasionally followed by a time — which
 * the "Date" column keeps showing verbatim. Sorting that text would order by
 * day-of-month, so the column sorts on this key instead: a fixed-width ISO
 * `YYYY-MM-DDTHH:mm:ss` string whose lexicographic order is chronological. An
 * ISO input (`2026-01-07`) is accepted as-is. Returns `null` for an empty or
 * unparseable value — the DataList comparator sorts `null` last in both
 * directions.
 *
 * @param referenceYear - Anchors the 2-digit-year century (defaults to the
 *                        current year); see {@link TWO_DIGIT_YEAR_FUTURE_WINDOW}.
 */
export function getDocumentDateSortKey(
  value: string | null | undefined,
  referenceYear: number = new Date().getFullYear(),
): string | null {
  const raw = (value ?? '').trim();
  if (raw === '') return null;

  const fr = FR_DATE_RE.exec(raw);
  if (fr) {
    const [, dd, mm, yy, hours, minutes, seconds] = fr;
    let year = Number(yy);
    if ((yy ?? '').length === 2) {
      year += referenceYear - (referenceYear % 100);
      if (year > referenceYear + TWO_DIGIT_YEAR_FUTURE_WINDOW) year -= 100;
    }
    return buildSortableDate(year, Number(mm), Number(dd), hours, minutes, seconds);
  }

  const iso = ISO_DATE_RE.exec(raw);
  if (iso) {
    const [, yyyy, mm, dd, hours, minutes, seconds] = iso;
    return buildSortableDate(Number(yyyy), Number(mm), Number(dd), hours, minutes, seconds);
  }

  return null;
}

/**
 * Format a byte count into a French human-readable size: "156 Ko", "2,4 Mo"…
 * 1 Ko = 1024 B, 1 Mo = 1024 Ko. A single decimal is shown for Mo and Go; Ko
 * and bytes are shown as integers. The decimal separator is a comma and a
 * non-breaking-friendly regular space precedes the unit.
 */
export function formatFileSize(bytes: number | null | undefined): string {
  if (bytes == null || !Number.isFinite(bytes) || bytes < 0) return '';
  if (bytes < 1024) return `${String(Math.round(bytes))} o`;

  const ko = bytes / 1024;
  if (ko < 1024) return `${String(Math.round(ko))} Ko`;

  const mo = ko / 1024;
  if (mo < 1024) return `${formatDecimalFr(mo)} Mo`;

  const go = mo / 1024;
  return `${formatDecimalFr(go)} Go`;
}

/** One-decimal French number ("2,4"); drops a trailing ",0". */
function formatDecimalFr(value: number): string {
  const rounded = Math.round(value * 10) / 10;
  return Number.isInteger(rounded)
    ? String(rounded)
    : rounded.toFixed(1).replace('.', ',');
}

/**
 * Estimate the decoded byte length of a base64 string without allocating the
 * full binary — `4` base64 chars encode `3` bytes, minus any `=` padding.
 */
export function base64ByteLength(b64: string | null | undefined): number {
  if (!b64) return 0;
  const clean = b64.replace(/[\r\n]/g, '');
  const padding = clean.endsWith('==') ? 2 : clean.endsWith('=') ? 1 : 0;
  return Math.max(0, Math.floor((clean.length * 3) / 4) - padding);
}

/** Decode a base64 string into the raw bytes it encodes. */
export function base64ToUint8Array(b64: string): Uint8Array {
  const clean = b64.replace(/[\r\n]/g, '');
  const binary = atob(clean);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
}

/**
 * Encode raw bytes as a base64 string. Chunked so very large attachments do
 * not blow the argument limit of `String.fromCharCode`.
 */
export function uint8ArrayToBase64(bytes: Uint8Array): string {
  const CHUNK = 0x8000; // 32 KiB
  let binary = '';
  for (let i = 0; i < bytes.length; i += CHUNK) {
    const slice = bytes.subarray(i, i + CHUNK);
    binary += String.fromCharCode(...slice);
  }
  return btoa(binary);
}

/** Decode a base64 string into a Blob of the given mime type. */
export function base64ToBlob(b64: string, contentType: string): Blob {
  const binary = atob(b64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  return new Blob([bytes], { type: contentType || 'application/octet-stream' });
}

/**
 * True when a browser renders this content type in a tab by itself (PDF,
 * images, text, JSON, XML). Anything else — Office, ZIP, Outlook `.msg`,
 * octet-stream… — is *downloaded* by the browser instead. Parameters
 * (`; charset=…`) and case are ignored.
 */
export function isBrowserDisplayableContentType(contentType: string): boolean {
  const ct = (contentType.split(';')[0] ?? '').trim().toLowerCase();
  return (
    ct === 'application/pdf' ||
    ct.startsWith('image/') ||
    ct.startsWith('text/') ||
    ct === 'application/json' ||
    ct === 'application/xml'
  );
}

/** Delay before revoking an object URL, so the tab/download can load it. */
const OBJECT_URL_REVOKE_DELAY_MS = 60_000;

/**
 * Open a base64 payload in a new browser tab.
 *
 * Only browser-displayable types (see {@link isBrowserDisplayableContentType})
 * open in a tab. Any other type would be downloaded by the new tab anyway — and
 * a `blob:` URL carries no file name, so it landed as a GUID-named file (mahhp
 * 22844). Those are downloaded directly instead, through an `<a download>`
 * carrying `fileName` so the file keeps its real name.
 */
export function openBase64InNewTab(
  b64: string,
  contentType: string,
  fileName?: string,
): void {
  const blob = base64ToBlob(b64, contentType);
  const url = URL.createObjectURL(blob);

  if (isBrowserDisplayableContentType(contentType)) {
    window.open(url, '_blank', 'noopener,noreferrer');
  } else {
    const link = document.createElement('a');
    link.href = url;
    // An empty `download` still downloads, under the browser's default name.
    link.download = fileName?.trim() ?? '';
    document.body.appendChild(link);
    link.click();
    link.remove();
  }

  // Revoke after a delay so the new tab / the download has time to load it.
  window.setTimeout(() => {
    URL.revokeObjectURL(url);
  }, OBJECT_URL_REVOKE_DELAY_MS);
}

/**
 * A data URI for inline preview with `<img>`.
 *
 * **Not for `<iframe src>`**: the deployed CSP sets `frame-src 'self' blob:`,
 * so a `data:` frame is refused (it fell back to `default-src 'self'` before
 * that directive existed). Framed previews go through `useBlobUrl` /
 * `base64ToBlob` instead. `img-src` does allow `data:`, so `<img>` is fine.
 * See `docs/SECURITY-HEADERS.md`.
 */
export function toDataUri(b64: string, contentType: string): string {
  return `data:${contentType || 'application/octet-stream'};base64,${b64}`;
}

/**
 * Format a VEOS email date as `dd/mm/yyyy à HH:mm`.
 *
 * VEOS sends either an ISO-ish timestamp or an already-formatted French date.
 * When the value cannot be parsed (or carries no time component) it is
 * returned unchanged so nothing is lost.
 */
export function formatEmailDate(value: string | undefined | null): string {
  if (!value) return '';
  const trimmed = value.trim();

  // Already "dd/mm/yyyy à HH:mm" or "dd/mm/yyyy" → keep as-is.
  if (/^\d{2}\/\d{2}\/\d{4}/.test(trimmed)) return trimmed;

  const parsed = new Date(trimmed);
  if (Number.isNaN(parsed.getTime())) return trimmed;

  const dd = String(parsed.getDate()).padStart(2, '0');
  const mm = String(parsed.getMonth() + 1).padStart(2, '0');
  const yyyy = String(parsed.getFullYear());
  const hh = String(parsed.getHours()).padStart(2, '0');
  const min = String(parsed.getMinutes()).padStart(2, '0');
  return `${dd}/${mm}/${yyyy} à ${hh}:${min}`;
}

/**
 * Detect whether an attachment file name is already present as a `NOM_ADOC`
 * in the documents list — if so it is already in GED and must not be re-GED'd.
 */
export function isFileNameInGed(
  fileName: string | undefined | null,
  documents: VeosDocumentRow[],
): boolean {
  if (!fileName) return false;
  const normalized = fileName.trim().toLowerCase();
  return documents.some(
    // Some VEOS rows carry a null `NOM_ADOC`; skip them rather than crash.
    (d) => d.NOM_ADOC?.trim().toLowerCase() === normalized,
  );
}
